// PRD 11 §3.4 / §3.5 / §3.6 — Add / Edit slide panel.
//
// Layout: 2-column (form + content blocks | web preview), collapses to one
// column on narrow viewports.
//
// Save flows:
//   • 임시저장 (Draft) → DRAFT, relaxed validation (title length only). Toast,
//     panel closes.
//   • 게시 (Publish)   → strict validation → confirm "게시 시 즉시 환자 앱에
//     노출됩니다…" → PUBLISHED. Every publish (initial or re-publish) confirms.
//   • 삭제 (Delete)    → edit mode + Root only; DeleteManualConfirm.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/app/store/auth.store';
import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { InfoModal } from '@/components/feedback/InfoModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';

import { useCreateManual, useUpdateManual } from '../hooks/use-manual-mutations';
import { useCategoryTreeQuery, useManualDetailQuery } from '../hooks/use-manuals';
import { useCounselingManualPageStore } from '../store/counseling-manual-page.store';
import {
  ManualApiError,
  type ConsultationManual,
  type ConsultationManualDetail,
  type ContentBlock,
  type ContentBlockType,
  type ManualCategoryTree,
  type ManualGrade,
  type ManualStatus,
} from '../types/counseling-manual.types';
import { getManualErrorMessage } from '../utils/error-message';
import { collectPreview, serializeBlocksToHtml } from '../utils/preview';
import {
  MAX_BLOCKS,
  validateManualForm,
  withoutEmptyMediaBlocks,
  type ManualFormDraft,
  type ManualFormErrors,
} from '../utils/validate-form';
import { CascadingCategorySelect, type CascadeField } from './CascadingCategorySelect';
import { ContentBlockList } from './ContentBlockList';
import { DeleteManualConfirm } from './DeleteManualConfirm';
import { GradeRadioGroup } from './GradeRadioGroup';
import { WebPreview } from './WebPreview';
import { useWriteGuard } from '@/shared/hooks/useWriteGuard';

type FormState = ManualFormDraft;

const EMPTY_FORM: FormState = {
  title: '',
  area: '',
  subArea: '',
  item: '',
  grade: null,
  blocks: [],
};

function formFromDetail(detail: ConsultationManualDetail | undefined): FormState {
  if (!detail) return EMPTY_FORM;
  return {
    title: detail.title,
    area: detail.area,
    subArea: detail.subArea,
    item: detail.item,
    grade: detail.grade,
    blocks: detail.blocks ?? [],
  };
}

// PRD §4.3 block id — Date.now() plus an in-instance sequence so rapid "Add"
// clicks don't collide.
let blockSeq = 0;
function nextBlockId(): number {
  blockSeq += 1;
  return Date.now() + blockSeq;
}

function buildBlock(type: ContentBlockType): ContentBlock {
  const base: ContentBlock = { id: nextBlockId(), type, title: '', text: '', showText: false };
  if (type === 'video') base.url = '';
  return base;
}

const TITLE_MAX = 200;
const EMPTY_TREE: ManualCategoryTree = { areas: [] };

export function ManualFormPanel() {
  const { t } = useTranslation('counseling-manual');
  const { canWrite, denyProps, tooltip } = useWriteGuard('CONTENT_LIB');
  // PRD §3.7 — delete requires CONTENT_LIB:DELETE, seeded to Root only.
  const canDelete = useAuthStore((s) => s.isRoot);

  const panel = useCounselingManualPageStore((s) => s.panel);
  const closePanel = useCounselingManualPageStore((s) => s.closePanel);

  const isOpen = panel !== null;
  const isEdit = panel?.mode === 'edit';
  const editingCid = panel?.mode === 'edit' ? panel.cid : null;

  const { data: tree = EMPTY_TREE } = useCategoryTreeQuery();
  const { data: detail, isLoading: isDetailLoading } = useManualDetailQuery(editingCid);

  const createMutation = useCreateManual();
  const updateMutation = useUpdateManual();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<ManualFormErrors>({});
  // Holds the publish confirm visibility (PRD §3.4 게시 확인 모달).
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  // Delete target for the in-panel 삭제 button (edit mode).
  const [deleteTarget, setDeleteTarget] = useState<ConsultationManual | null>(null);
  // SHCT-843 — the (항목, 단계) slot is already published. Holds the resolved sentence rather than a
  // boolean, because it carries the occupying manual's CID and title from the server's `meta`.
  const [duplicateNotice, setDuplicateNotice] = useState<string | null>(null);

  // Repopulate whenever the panel target changes (create ↔ edit, or detail
  // finishes loading).
  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setShowPublishConfirm(false);
    if (isEdit) {
      if (detail) setForm(formFromDetail(detail));
    } else {
      setForm(EMPTY_FORM);
    }
  }, [isOpen, isEdit, detail]);

  useEffect(() => {
    setIsDirty(false);
    setShowDiscard(false);
  }, [isOpen, isEdit, editingCid]);

  // PRD §7 — CONTENT_LIB:READ opens a manual as a read-only detail view: every
  // input is locked (the footer's write actions are already disabled). Also
  // locked while an edit's detail is still loading.
  const fieldsDisabled = !canWrite || (isEdit && isDetailLoading);

  const title = isEdit ? t('form.titleEdit') : t('form.titleAdd');
  const cidLabel = isEdit ? editingCid ?? '' : t('form.fields.cid.placeholder');
  const categoryPath = [form.area, form.subArea, form.item].filter(Boolean).join(' / ');

  const updateTitle = (next: string) => {
    setForm((f) => ({ ...f, title: next.slice(0, TITLE_MAX) }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, title: undefined }));
  };

  const updateCascade = (next: { area: string; subArea: string; item: string }) => {
    setForm((f) => ({ ...f, area: next.area, subArea: next.subArea, item: next.item }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, cascade: undefined }));
  };

  const updateGrade = (g: ManualGrade) => {
    setForm((f) => ({ ...f, grade: g }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, grade: undefined }));
  };

  // [공통] 00_입력검증표준 §1.2 — "검증 시점: 필드 이탈(blur) 시 해당 필드 검증
  // 메시지 노출". These are display-time hints only: they never block 임시저장,
  // which stays title-only per PRD §3.4 (validateManualForm's `forPublish` flag
  // still decides what actually gates a save, and re-validating on save replaces
  // whatever these set).
  const handleTitleBlur = () => {
    if (fieldsDisabled) return;
    if (!form.title.trim()) setErrors((prev) => ({ ...prev, title: t('errors.TITLE_REQUIRED') }));
  };

  // PRD §4.2 defines a per-field message for each cascade select (영역/하위영역/
  // 항목을 선택해주세요.); the combined §8.1 message stays for the publish check.
  // Each level only complains once its parent is chosen — an unselected 하위영역
  // is not yet a problem while 영역 is still empty.
  const handleCascadeBlur = (field: CascadeField) => {
    if (fieldsDisabled) return;
    setErrors((prev) => {
      if (field === 'area' && !form.area) return { ...prev, cascade: t('errors.AREA_REQUIRED') };
      if (field === 'subArea' && form.area && !form.subArea) {
        return { ...prev, cascade: t('errors.SUB_AREA_REQUIRED') };
      }
      if (field === 'item' && form.area && form.subArea && !form.item) {
        return { ...prev, cascade: t('errors.ITEM_REQUIRED') };
      }
      return prev;
    });
  };

  const addBlock = (type: ContentBlockType) => {
    setForm((f) => (f.blocks.length >= MAX_BLOCKS ? f : { ...f, blocks: [...f.blocks, buildBlock(type)] }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, blocks: undefined }));
  };

  const updateBlock = (id: number, patch: Partial<ContentBlock>) => {
    setForm((f) => ({ ...f, blocks: f.blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)) }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, blocks: undefined }));
  };

  const deleteBlock = (id: number) => {
    setForm((f) => ({ ...f, blocks: f.blocks.filter((b) => b.id !== id) }));
    setIsDirty(true);
    setErrors((prev) => ({ ...prev, blocks: undefined }));
  };

  // PRD §3.6 — reorder blocks; preview follows the new sequence.
  const reorderBlock = (from: number, to: number) => {
    setForm((f) => {
      const next = [...f.blocks];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return { ...f, blocks: next };
    });
    setIsDirty(true);
  };

  const applyApiError = (e: unknown) => {
    // SHCT-843 — admin-web §7.1 classes `*_DUPLICATE` as a Business Logic Error, whose required
    // channel is a Modal, not a toast: the publish did NOT happen and the operator has to decide
    // what to do about the manual already in the slot. The server names that manual in
    // `meta.cid` · `meta.title`, so the modal says which one rather than only that the slot is
    // taken — that identifier is the whole point of the rule being visible at all. The sentence
    // itself is PRD 11's, not the server's longer one; the second re-test failed on that.
    if (e instanceof ManualApiError && e.code === 'MANUAL_DUPLICATE') {
      const cid = typeof e.meta?.cid === 'string' ? e.meta.cid : null;
      const occupantTitle = typeof e.meta?.title === 'string' ? e.meta.title : null;
      setDuplicateNotice(
        t('errors.MANUAL_DUPLICATE', {
          // Empty when the server did not identify the occupant, which drops the parenthetical
          // instead of rendering an empty "()".
          occupant: cid && occupantTitle ? ` (${cid} ${occupantTitle})` : '',
        }),
      );
      return;
    }
    // admin-web §7.1 — Validation (field) errors are inline-only; System errors
    // surface as a toast. So a field-carrying ManualApiError sets inline errors
    // and does NOT also toast; everything else toasts.
    if (e instanceof ManualApiError && e.fields) {
      const localized = getManualErrorMessage(e, t, isEdit ? 'toasts.updateError' : 'toasts.saveError');
      setErrors((prev) => ({
        ...prev,
        ...(e.fields?.title ? { title: localized } : {}),
        ...(e.fields?.cascade ? { cascade: localized } : {}),
        ...(e.fields?.grade ? { grade: localized } : {}),
      }));
      return;
    }
    toast.error(getManualErrorMessage(e, t, isEdit ? 'toasts.updateError' : 'toasts.saveError'));
  };

  const performSave = async (status: ManualStatus) => {
    if (isPending) return;
    const savedBlocks = withoutEmptyMediaBlocks(form.blocks);
    try {
      const input = {
        title: form.title.trim(),
        area: form.area,
        subArea: form.subArea,
        item: form.item,
        // Send the chosen grade as-is; a DRAFT may have none (null). Publish is
        // grade-validated before we get here, so it's non-null on publish.
        grade: form.grade,
        // SHCT-662 / PRD 11 v1.10 §3 — an image block with no upload and a video block with
        // no URL are excluded from the payload. 상담매뉴얼 allows 0 blocks, so nothing here can
        // block the save; what this prevents is the BE rejecting the empty media block and the
        // preview / contentHtml being built from blocks that will never render.
        blocks: savedBlocks,
        preview: collectPreview(savedBlocks),
        contentHtml: serializeBlocksToHtml(savedBlocks),
        status,
      };

      if (isEdit && editingCid) {
        await updateMutation.mutateAsync({ cid: editingCid, input });
      } else {
        await createMutation.mutateAsync(input);
      }

      setIsDirty(false);
      toast.success(status === 'PUBLISHED' ? t('toasts.published') : t('toasts.savedDraft'));
      closePanel();
    } catch (e) {
      applyApiError(e);
    }
  };

  const handleSaveClick = (status: ManualStatus) => {
    if (!canWrite || isPending) return;
    const result = validateManualForm(form, t, status === 'PUBLISHED');
    setErrors(result.errors);
    if (!result.ok) return;

    if (status === 'PUBLISHED') {
      // PRD §3.4 — confirm before every publish.
      setShowPublishConfirm(true);
      return;
    }
    void performSave('DRAFT');
  };

  const handlePanelClose = () => {
    if (isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    closePanel();
  };

  // Build a ConsultationManual-shaped value for the in-panel delete confirm.
  const deleteSubject = useMemo<ConsultationManual | null>(() => {
    if (!detail) return null;
    return {
      cid: detail.cid,
      title: detail.title,
      grade: detail.grade,
      area: detail.area,
      subArea: detail.subArea,
      item: detail.item,
      preview: detail.preview,
      status: detail.status,
      publishedAt: detail.publishedAt,
    };
  }, [detail]);

  return (
    <>
      <SlidePanel open={isOpen} title={title} width={860} onClose={handlePanelClose}>
        <div className="grid gap-6 grid-cols-[1.4fr_1fr] max-[900px]:grid-cols-1">
          {/* === Left column: form + content blocks === */}
          <div>
            {/* CID — readonly. */}
            <div className="form-field">
              <label className="form-field__label" htmlFor="cmCid">
                {t('form.fields.cid.label')}
              </label>
              <input
                id="cmCid"
                type="text"
                className="form-field__input"
                value={cidLabel}
                disabled
                style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
              />
            </div>

            {/* Title — required at publish, 200 char cap. */}
            <div className="form-field">
              <label className="form-field__label" htmlFor="cmTitle">
                {t('form.fields.title.label')} <span className="required">*</span>
              </label>
              <input
                id="cmTitle"
                type="text"
                className={`form-field__input${errors.title ? ' form-field__input--error' : ''}`}
                value={form.title}
                onChange={(e) => updateTitle(e.target.value)}
                maxLength={TITLE_MAX}
                placeholder={t('form.fields.title.placeholder')}
                aria-invalid={errors.title ? 'true' : undefined}
                disabled={fieldsDisabled}
                onBlur={handleTitleBlur}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <p className="form-field__error" style={{ minHeight: 16 }}>
                  {errors.title ?? ''}
                </p>
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 4, whiteSpace: 'nowrap' }}>
                  {form.title.length}/{TITLE_MAX}
                </span>
              </div>
            </div>

            {/* Cascade — required at publish. */}
            <div className="form-field">
              <label className="form-field__label">
                {t('form.fields.cascade.label')} <span className="required">*</span>
              </label>
              <CascadingCategorySelect
                tree={tree}
                area={form.area}
                subArea={form.subArea}
                item={form.item}
                onChange={updateCascade}
                onFieldBlur={handleCascadeBlur}
                disabled={fieldsDisabled}
              />
              {errors.cascade ? <p className="form-field__error">{errors.cascade}</p> : null}
            </div>

            {/* Grade — required at publish. */}
            <div className="form-field">
              <label className="form-field__label">
                {t('form.fields.grade.label')} <span className="required">*</span>
              </label>
              <GradeRadioGroup
                value={form.grade}
                onChange={updateGrade}
                invalid={Boolean(errors.grade)}
                disabled={fieldsDisabled}
              />
              {errors.grade ? <p className="form-field__error">{errors.grade}</p> : null}
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '24px 0 16px' }} />
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--foreground)' }}>
              {t('form.sections.content')}
            </h3>

            <ContentBlockList
              blocks={form.blocks}
              error={errors.blocks}
              disabled={fieldsDisabled}
              onAddText={() => addBlock('text')}
              onAddImage={() => addBlock('image')}
              onAddVideo={() => addBlock('video')}
              onChangeBlock={updateBlock}
              onDeleteBlock={deleteBlock}
              onReorder={reorderBlock}
            />
          </div>

          {/* === Right column: 의료진 웹 미리보기 ===
              `minWidth: 0` so long preview content can't blow out the grid
              column and widen the panel (grid children default to min-width:auto). */}
          <div style={{ minWidth: 0 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: 'var(--foreground)' }}>
              {t('form.sections.preview')}
            </h3>
            {/* SHCT-456 — edit mode shows the manual's DB last-modified; create
                mode passes null so the preview falls back to today. */}
            <WebPreview
              title={form.title}
              cid={cidLabel}
              categoryPath={categoryPath}
              blocks={form.blocks}
              updatedAt={isEdit ? (detail?.updatedAt ?? null) : null}
            />
          </div>
        </div>

        {/* Footer — 삭제 (edit + Root) · 임시저장 · 게시. PRD §8.3: a role without
            WRITE sees the write buttons DISABLED with a tooltip, not hidden. */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 32,
            paddingTop: 16,
            borderTop: '1px solid var(--border)',
          }}
        >
          {isEdit ? (
            <button
              type="button"
              className="btn btn-default btn-destructive"
              style={{ marginRight: 'auto' }}
              onClick={() => setDeleteTarget(deleteSubject)}
              disabled={!canDelete || isPending || isDetailLoading || !deleteSubject}
              title={canDelete ? undefined : t('form.actions.forbidden')}
            >
              {t('form.actions.delete')}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={() => handleSaveClick('DRAFT')}
            disabled={!canWrite || isPending || (isEdit && isDetailLoading)}
            {...denyProps}
          >
            {t('form.actions.saveDraft')}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={() => handleSaveClick('PUBLISHED')}
            disabled={!canWrite || isPending || (isEdit && isDetailLoading)}
            {...denyProps}
          >
            {t('form.actions.publish')}
          </button>
        </div>
      </SlidePanel>

      {/* PRD §3.4 — publish confirm. */}
      <ConfirmModal
        open={showPublishConfirm}
        title={t('publishConfirm.title')}
        message={t('publishConfirm.message')}
        confirmLabel={t('publishConfirm.confirm')}
        cancelLabel={t('publishConfirm.cancel')}
        loading={isPending}
        onConfirm={() => {
          setShowPublishConfirm(false);
          void performSave('PUBLISHED');
        }}
        onCancel={() => setShowPublishConfirm(false)}
      />

      {/* Discard-changes confirm. */}
      <ConfirmModal
        open={showDiscard}
        title={t('discardConfirm.title')}
        message={t('discardConfirm.message')}
        confirmLabel={t('discardConfirm.confirm')}
        cancelLabel={t('discardConfirm.cancel')}
        confirmVariant="destructive"
        onConfirm={() => {
          setShowDiscard(false);
          setIsDirty(false);
          closePanel();
        }}
        onCancel={() => setShowDiscard(false)}
      />

      {/* SHCT-843 — (항목, 단계) slot already published. §7.1 Business Logic Error → Modal. The panel
          stays open and keeps the draft, so the operator can change the 항목/단계 and publish again. */}
      <InfoModal
        open={duplicateNotice !== null}
        title={t('errors.MANUAL_DUPLICATE_TITLE')}
        body={duplicateNotice ?? ''}
        onClose={() => setDuplicateNotice(null)}
      />

      {/* In-panel delete (edit mode). Closes the panel on success. */}
      <DeleteManualConfirm
        manual={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={closePanel}
      />
    </>
  );
}
